import { Stack, Typography } from '@mui/material'
import { Button, Select } from 'utoniq-ui'

import { ArtistCreatePreSaleInput } from '@/clients/utoniq-core/schema'
import { useTranslation } from '@/core'
import { Organism } from '@/core/architecture'
import { Form, tLabel, useForm, UseFormProps } from '@/core/form'

import { DatePickerCell } from './components/DatePickerCell'
import { PaymentMethodsInputCell } from './components/PaymentMethodsInputCell'
import { TextInputCell } from './components/TextInputCell'
import { ToggleInputCell } from './components/ToggleInputCell'

export type LotteryCreateValues = Omit<
  ArtistCreatePreSaleInput,
  'paymentMethods'
> & {
  purchasableCount: number
  maxChoice: number
  applicationStartedAt: string
  applicationEndedAt: string
  resultNoticedAt: string
  purchasableStartedAt: string
  purchasableEndedAt: string
  paymentMethods: string
}

const schema: UseFormProps['schema'] = o => ({
  title: o
    .string()
    .max(30)
    .label(tLabel('consolePreSaleCreate.title'))
    .required(),
  description: o
    .string()
    .max(5000)
    .label(tLabel('consolePreSaleCreate.description'))
    .required(),
  note: o
    .string()
    .max(5000)
    .label(tLabel('consolePreSaleCreate.note'))
    .required(),
  purchasableCount: o
    .number()
    .label(tLabel('consolePreSaleCreate.purchasableCount'))
    .max(8)
    .required(),
  maxChoice: o
    .number()
    .label(tLabel('consolePreSaleCreate.maxChoice'))
    .required(),
  applicationStartedAt: o
    .date()
    .label(tLabel('consolePreSaleCreate.applicationStartedAt'))
    .required(),
  applicationEndedAt: o
    .date()
    .label(tLabel('consolePreSaleCreate.applicationEndedAt'))
    .required(),
  resultNoticedAt: o
    .date()
    .label(tLabel('consolePreSaleCreate.resultNoticedAt'))
    .required(),
  paymentMethods: o
    .string()
    .optional()
    .label('consolePreSaleCreate.paymentMethods'),
  purchasableStartedAt: o
    .date()
    .label(tLabel('consolePreSaleCreate.purchasableStartedAt'))
    .required(),
  purchasableEndedAt: o
    .date()
    .label(tLabel('consolePreSaleCreate.purchasableEndedAt'))
    .required(),
  isPublished: o.bool().label(tLabel('consolePreSaleCreate.isPublished')),
  startedAt: o
    .date()
    .label(tLabel('consolePreSaleCreate.startedAt'))
    .required(),
  endedAt: o.date().label(tLabel('consolePreSaleCreate.endedAt')).required(),
})

export const LotteryCreateForm: Organism<{
  submitting: boolean
  onSubmit: (values: LotteryCreateValues) => Promise<void>
}> = ({ onSubmit, submitting }) => {
  const { t } = useTranslation('consolePreSaleCreate')

  const {
    handleSubmit,
    formState: { errors, isValid },
    register,
    setValue,
    watch,
  } = useForm<LotteryCreateValues>({
    schema,
    defaultValues,
  })

  return (
    <Form onSubmit={handleSubmit(onSubmit)} sx={{ pt: 6, pb: 9 }}>
      <TextInputCell type="text" error={errors.title} {...register('title')} />
      <TextInputCell
        type="textarea"
        textFieldProps={{
          multiline: true,
          rows: 5,
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        error={errors.description}
        {...register('description')}
      />
      <TextInputCell
        type="textarea"
        textFieldProps={{
          multiline: true,
          rows: 5,
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        error={errors.note}
        {...register('note')}
      />
      <TextInputCell
        type="tel"
        error={errors.purchasableCount}
        {...register('purchasableCount')}
      />
      {/* 最大希望数 */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('maxChoice')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('maxChoiceDescription')}
      </Typography>
      <Select
        listItems={[
          { value: '1', label: '第1希望まで' },
          { value: '2', label: '第2希望まで' },
          { value: '3', label: '第3希望まで' },
        ]}
        value={watch('maxChoice').toString()}
        onChange={value => {
          setValue('maxChoice', Number(value))
        }}
        formControlProps={{
          sx: {
            width: '100%',
            bgcolor: 'grey.50',
            minHeight: 'auto',
            mb: 16,
          },
        }}
      />
      {/* 応募可能なユーザー
      TODO: メンバーシップ対応API実装後に着手 https://app.clickup.com/t/908127/UT-14149
       */}
      {/* <Typography variant="h6" sx={{ mb: 2, mt: 7 }}>
        {t('membershipAccessPermission')}
      </Typography>
      <Select
        listItems={[
          { value: '全員', label: '全員' },
          { value: 'メンバーシップ', label: 'メンバーシップ' },
        ]}
        value={watch('membershipAccessPermission').toString()}
        onChange={value => {
          setValue('membershipAccessPermission', value)
        }}
        formControlProps={{
          sx: {
            width: '100%',
            bgcolor: 'grey.50',
            minHeight: 'auto',
            mb: 16,
          },
        }}
      /> */}

      {/* 先行抽選受付期間 */}
      <Typography variant="h5" sx={{ mb: 4, mt: 8 }}>
        {t('applicationPeriod')}
      </Typography>
      <DatePickerCell
        name="applicationStartedAt"
        desc={t('applicationStartedAtDesc')}
        error={errors.applicationStartedAt}
        setValue={setValue}
        value={watch('applicationStartedAt')}
      />
      <DatePickerCell
        name="applicationEndedAt"
        error={errors.applicationEndedAt}
        setValue={setValue}
        value={watch('applicationEndedAt')}
      />
      {/* 先行抽選結果通知日 */}
      <DatePickerCell
        name="resultNoticedAt"
        desc={t('resultNoticedAtDesc')}
        error={errors.resultNoticedAt}
        setValue={setValue}
        value={watch('resultNoticedAt')}
      />
      {/* 決済方法設定 */}
      <PaymentMethodsInputCell
        paymentMethods={watch('paymentMethods') as unknown as string}
        setValue={setValue}
      />
      {/* 決済期間 */}
      <Typography variant="h5" sx={{ mb: 4 }}>
        {t('purchasablePeriod')}
      </Typography>
      <DatePickerCell
        name="purchasableStartedAt"
        desc={t('purchasableStartedAtDesc')}
        error={errors.purchasableStartedAt}
        setValue={setValue}
        value={watch('purchasableStartedAt')}
      />
      <DatePickerCell
        name="purchasableEndedAt"
        error={errors.purchasableEndedAt}
        setValue={setValue}
        value={watch('purchasableEndedAt')}
      />
      {/* 公開設定 */}
      <ToggleInputCell
        content={{
          title: t('isPublishedTitle'),
          description: t('isPublishedDesc'),
        }}
        {...register('isPublished')}
      />
      <DatePickerCell
        name="startedAt"
        error={errors.startedAt}
        setValue={setValue}
        value={watch('startedAt')}
      />
      <DatePickerCell
        name="endedAt"
        error={errors.endedAt}
        setValue={setValue}
        value={watch('endedAt')}
      />

      {/* 送信ボタン */}
      <Stack sx={{ mt: 5, gap: 5 }} alignItems="center">
        <Button
          type="submit"
          size="large"
          sx={{ width: 244 }}
          loading={submitting}
          disabled={!isValid || submitting}
        >
          {t('submit')}
        </Button>
      </Stack>
    </Form>
  )
}

const defaultValues = {
  maxChoice: 1,
  paymentMethods: 'both',
}
